import OpenSeadragon from 'openseadragon';
import api from '../API';
import { extractData } from '../utils/apiResponse';

const SAS_SKEW_MS = 60 * 1000;

function signedUrl(path, sas) {
    if (!sas) return path;
    const sep = path.includes('?') ? '&' : '?';
    return `${path}${sep}${sas}`;
}

function dziFilesPrefix(dziPath) {
    const path = (dziPath || '').split('?')[0];
    const base = path.toLowerCase().endsWith('.dzi') ? path.slice(0, -4) : path;
    return `${base}_files/`;
}

function parseDziXml(text) {
    const doc = new DOMParser().parseFromString(text, 'application/xml');
    if (doc.querySelector('parsererror')) {
        throw new Error('Invalid DZI XML');
    }
    const image = doc.querySelector('Image');
    const size = doc.querySelector('Size');
    if (!image || !size) {
        throw new Error('DZI XML missing Image/Size');
    }
    const width = Number(size.getAttribute('Width'));
    const height = Number(size.getAttribute('Height'));
    const tileSize = Number(image.getAttribute('TileSize'));
    const overlap = Number(image.getAttribute('Overlap') || 0);
    const format = (image.getAttribute('Format') || 'jpeg').toLowerCase();
    if (!width || !height || !tileSize) {
        throw new Error('DZI XML had invalid dimensions');
    }
    const maxLevel = Math.ceil(Math.log2(Math.max(width, height)));
    return { width, height, tileSize, overlap, format, maxLevel };
}

export async function fetchPathPresenterSas(itemId) {
    const resp = await api.get(`pathpresenter/item/${itemId}/sas`);
    const data = extractData(resp);
    if (!data?.sas || !data?.dziPath) {
        throw new Error(resp?.data?.message || 'Failed to mint PathPresenter SAS');
    }
    return data;
}

async function fetchDzi(dziPath, sas) {
    const resp = await fetch(signedUrl(dziPath, sas));
    if (!resp.ok) {
        throw new Error(`DZI download failed (${resp.status})`);
    }
    return parseDziXml(await resp.text());
}

export class PathPresenterTileSource extends OpenSeadragon.TileSource {
    constructor({ itemId, meta, sasInfo, dzi }) {
        super({
            width: dzi.width,
            height: dzi.height,
            tileSize: dzi.tileSize,
            tileOverlap: dzi.overlap || 0,
            minLevel: 0,
            maxLevel: dzi.maxLevel,
        });
        this.itemId = itemId;
        this.meta = meta || {};
        this.sas = sasInfo.sas;
        this.seMs = sasInfo.se ? Date.parse(sasInfo.se) : null;
        this.dziPath = sasInfo.dziPath || meta?.dziPath;
        this.filesPrefix = dziFilesPrefix(this.dziPath);
        this.fileFormat = dzi.format || 'jpeg';
        const mpp = Number(meta?.mdMpp);
        if (Number.isFinite(mpp) && mpp > 0) {
            this.mpp = { x: mpp, y: mpp };
        }
    }

    getTileUrl(level, x, y) {
        const path = `${this.filesPrefix}${level}/${x}_${y}.${this.fileFormat}`;
        return signedUrl(path, this.sas);
    }

    getTileAjaxHeaders() {
        return {};
    }

    async refreshSasIfNeeded(force = false) {
        if (!force && this.seMs && Date.now() < this.seMs - SAS_SKEW_MS) {
            return false;
        }
        const info = await fetchPathPresenterSas(this.itemId);
        this.sas = info.sas;
        this.seMs = info.se ? Date.parse(info.se) : null;
        if (info.dziPath) {
            this.dziPath = info.dziPath;
            this.filesPrefix = dziFilesPrefix(info.dziPath);
        }
        return true;
    }
}

export async function createPathPresenterTileSource(itemId, meta) {
    const sasInfo = await fetchPathPresenterSas(itemId);
    const dzi = await fetchDzi(sasInfo.dziPath || meta?.dziPath, sasInfo.sas);
    return new PathPresenterTileSource({ itemId, meta, sasInfo, dzi });
}

export function pathPresenterMetaFromItem(item) {
    const pp = item?.meta?.pathpresenter;
    if (pp && pp.dziPath) return pp;
    return null;
}
